// src/admin/OrderTrackingModal.js
// مكون نافذة تتبع الطلب والسائق
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { EnhancedGoogleMap, Marker, DirectionsRenderer } from './../useGoogleMaps';

const OrderTrackingModal = ({ orderId, onClose }) => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [directions, setDirections] = useState(null);

  // إعداد مراقبة موقع السائق
  const setupDriverTracking = useCallback((driverId) => {
    const driverUnsubscribe = onSnapshot(
      doc(db, 'drivers', driverId),
      (driverDoc) => {
        if (driverDoc.exists()) {
          const driverData = driverDoc.data();
          if (driverData.currentLocation) {
            setDriverLocation(parseLocation(driverData.currentLocation));
          }
        }
      },
      (err) => {
        console.error('خطأ في مراقبة موقع السائق:', err);
      }
    );
    
    return driverUnsubscribe;
  }, []);

  useEffect(() => {
    if (!orderId) return;

    setLoading(true);
    
    // مراقبة الطلب للحصول على التحديثات في الوقت الفعلي
    const orderUnsubscribe = onSnapshot(
      doc(db, 'orders', orderId),
      (orderDoc) => {
        if (orderDoc.exists()) {
          const data = { id: orderDoc.id, ...orderDoc.data() };
          setOrderData(data);
          
          // إذا كان هناك موقع للسائق في بيانات الطلب
          if (data.driverLocation) {
            setDriverLocation(parseLocation(data.driverLocation));
          }
          
          // إذا كان هناك معرف سائق، نقوم بمراقبة موقع السائق
          if (data.driverId) {
            setupDriverTracking(data.driverId);
          }
          
          // إذا كان هناك موقعان، نقوم بإنشاء الاتجاهات
          if (data.restaurantLocation && data.customerLocation) {
            updateDirections(
              parseLocation(data.restaurantLocation),
              parseLocation(data.customerLocation)
            );
          }
          
          setLoading(false);
        } else {
          setError('لم يتم العثور على الطلب');
          setLoading(false);
        }
      },
      (err) => {
        console.error('خطأ في مراقبة الطلب:', err);
        setError(`خطأ في تحميل بيانات الطلب: ${err.message}`);
        setLoading(false);
      }
    );
    
    return () => {
      orderUnsubscribe();
    };
  }, [orderId, setupDriverTracking]);
  
  // تحويل أشكال مختلفة من كائنات الموقع إلى النموذج القياسي { lat, lng }
  const parseLocation = (location) => {
    if (!location) return null;
    
    try {
      // حالة GeoPoint من Firestore
      if (typeof location.latitude === 'number' && typeof location.longitude === 'number') {
        return { lat: location.latitude, lng: location.longitude };
      }
      
      // حالة كائن { lat, lng }
      if (typeof location.lat !== 'undefined' && typeof location.lng !== 'undefined') {
        const lat = typeof location.lat === 'function' ? location.lat() : Number(location.lat);
        const lng = typeof location.lng === 'function' ? location.lng() : Number(location.lng);
        return { lat, lng };
      }
      
      // حالة المصفوفة [lng, lat]
      if (Array.isArray(location) && location.length === 2) {
        return { lat: location[1], lng: location[0] };
      }
      
      console.warn('تنسيق موقع غير معروف:', location);
      return null;
    } catch (error) {
      console.error('خطأ في تحليل بيانات الموقع:', error);
      return null;
    }
  };
  
  // تحديث الاتجاهات بين نقطتين
  const updateDirections = (origin, destination) => {
    if (!window.google || !origin || !destination) return;
    
    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: new window.google.maps.LatLng(origin.lat, origin.lng),
        destination: new window.google.maps.LatLng(destination.lat, destination.lng),
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          console.error('خطأ في الحصول على الاتجاهات:', status);
        }
      }
    );
  };
  
  // الحصول على لون مسار التنقل حسب حالة الطلب
  const getRouteColor = () => {
    if (!orderData) return '#4a90e2'; // اللون الافتراضي
    
    if (orderData.status === 'تم التسليم ✅') return '#4caf50';
    if (orderData.status === 'تم الاستلام 🚗') return '#2979ff';
    if (orderData.status === 'قيد التنفيذ 🚀') return '#ff9800';
    if (orderData.status === 'ملغي ❌') return '#f44336';
    
    return '#4a90e2';
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="tracking-modal">
          <div className="modal-header">
            <h2>تتبع الطلب</h2>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>
          <div className="tracking-loading">
            <div className="spinner"></div>
            <p>جاري تحميل بيانات التتبع...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="modal-overlay">
        <div className="tracking-modal">
          <div className="modal-header">
            <h2>تتبع الطلب</h2>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>
          <div className="tracking-error">
            <p>{error}</p>
            <button className="button primary" onClick={onClose}>إغلاق</button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!orderData) {
    return (
      <div className="modal-overlay">
        <div className="tracking-modal">
          <div className="modal-header">
            <h2>تتبع الطلب</h2>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>
          <div className="tracking-error">
            <p>لا توجد بيانات متاحة لهذا الطلب</p>
            <button className="button primary" onClick={onClose}>إغلاق</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="tracking-modal">
        <div className="modal-header">
          <h2>تتبع الطلب {orderData.orderCode || `#${orderData.id.substring(0, 8)}`}</h2>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        
        <div className="tracking-status">
          <div className="tracking-info">
            <div className="tracking-status-item">
              <span className="status-label">حالة الطلب:</span>
              <span className={`status-badge ${
                orderData.status === 'تم التسليم ✅' ? 'status-delivered' : 
                orderData.status === 'تم الاستلام 🚗' ? 'status-pickup' :
                orderData.status === 'ملغي ❌' ? 'status-cancelled' : 'status-pending'
              }`}>
                {orderData.status || 'غير معروف'}
              </span>
            </div>
            
            <div className="tracking-status-item">
              <span className="status-label">السائق:</span>
              <span className="status-value">{orderData.driverName || 'غير معين'}</span>
            </div>
            
            <div className="tracking-status-item">
              <span className="status-label">المطعم:</span>
              <span className="status-value">{orderData.restaurantName || 'غير معروف'}</span>
            </div>
            
            <div className="tracking-status-item">
              <span className="status-label">العميل:</span>
              <span className="status-value">{orderData.customerName || 'غير معروف'}</span>
            </div>
          </div>
        </div>
        
        <div className="tracking-map">
          <EnhancedGoogleMap 
            center={driverLocation || parseLocation(orderData.restaurantLocation) || { lat: 29.3759, lng: 47.9774 }}
            zoom={13}
            mapStyles={{
              width: '100%',
              height: '400px',
              borderRadius: '8px'
            }}
          >
            {/* المطعم */}
            {orderData.restaurantLocation && parseLocation(orderData.restaurantLocation) && (
              <Marker
                position={parseLocation(orderData.restaurantLocation)}
                icon={{
                  url: '/icons/restaurant-marker.png',
                  scaledSize: { width: 40, height: 40 }
                }}
                label={{
                  text: "المطعم",
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  className: 'marker-label'
                }}
              />
            )}
            
            {/* العميل */}
            {orderData.customerLocation && parseLocation(orderData.customerLocation) && (
              <Marker
                position={parseLocation(orderData.customerLocation)}
                icon={{
                  url: '/icons/customer-marker.png',
                  scaledSize: { width: 40, height: 40 }
                }}
                label={{
                  text: "العميل",
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  className: 'marker-label'
                }}
              />
            )}
            
            {/* السائق */}
            {driverLocation && (
              <Marker
                position={driverLocation}
                icon={{
                  url: '/icons/driver-marker.png',
                  scaledSize: { width: 50, height: 50 }
                }}
                label={{
                  text: "السائق",
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  className: 'marker-label'
                }}
              />
            )}
            
            {/* رسم المسار */}
            {directions && (
              <DirectionsRenderer
                directions={directions}
                options={{
                  suppressMarkers: true,
                  polylineOptions: {
                    strokeColor: getRouteColor(),
                    strokeWeight: 5,
                    strokeOpacity: 0.8
                  }
                }}
              />
            )}
          </EnhancedGoogleMap>
          
          <div className="tracking-legend">
            <div className="legend-item">
              <div className="legend-icon restaurant"></div>
              <span>المطعم</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon customer"></div>
              <span>العميل</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon driver"></div>
              <span>السائق</span>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button className="button primary" onClick={onClose}>إغلاق</button>
        </div>
      </div>
    </div>
  );
};

export default OrderTrackingModal;